'use client'

import { useEffect, useState } from 'react'
import { ROADMAP_ITEMS, type RoadmapStatus } from '@/lib/roadmap'

const COLS: { status: RoadmapStatus; label: string; color: string; note: string }[] = [
  { status: 'in_progress', label: 'In progress', color: '#7c5cff', note: 'Being built right now.' },
  { status: 'planned', label: 'Planned', color: '#0ea5e9', note: 'Committed and coming.' },
  { status: 'exploring', label: 'Exploring', color: '#f59e0b', note: 'Ideas we’re weighing — vote to push them up.' },
  { status: 'shipped', label: 'Shipped', color: '#10b981', note: 'Live now.' },
]

const STORAGE_KEY = 'papr_roadmap_voter'

export function RoadmapBoard() {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [voted, setVoted] = useState<string[]>([])
  const [voterKey, setVoterKey] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    let key = localStorage.getItem(STORAGE_KEY) || ''
    if (!key) {
      key = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now()}`
      localStorage.setItem(STORAGE_KEY, key)
    }
    setVoterKey(key)
    fetch(`/api/roadmap/votes?voterKey=${encodeURIComponent(key)}`)
      .then((r) => r.json())
      .then((d) => {
        setCounts(d.counts || {})
        setVoted(d.voted || [])
      })
      .catch(() => {})
  }, [])

  async function toggle(itemId: string) {
    if (!voterKey || busy) return
    setBusy(itemId)
    try {
      const r = await fetch('/api/roadmap/votes', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ itemId, voterKey }),
      })
      const d = await r.json()
      if (r.ok) {
        setCounts(d.counts || {})
        setVoted(d.voted || [])
      }
    } catch {
      /* ignore — non-critical */
    }
    setBusy(null)
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(228px, 1fr))', gap: 18, alignItems: 'start' }}>
      {COLS.map((col) => {
        const items = ROADMAP_ITEMS.filter((i) => i.status === col.status)
        return (
          <section key={col.status} aria-label={col.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 3 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: col.color, display: 'inline-block' }} />
              <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em', margin: 0, color: '#0a0a0b' }}>{col.label}</h3>
              <span style={{ fontSize: 12, color: '#a59f93', fontFamily: 'var(--font-geist-mono), "Geist Mono", ui-monospace, monospace' }}>{items.length}</span>
            </div>
            <p style={{ fontSize: 12, color: '#7a7468', margin: '0 0 14px' }}>{col.note}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              {items.map((it) => {
                const votable = col.status !== 'shipped'
                const isVoted = voted.includes(it.id)
                const n = counts[it.id] || 0
                return (
                  <div key={it.id} style={{ background: '#fffdf8', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 14, padding: '14px 15px' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#0a0a0b', letterSpacing: '-0.01em' }}>{it.title}</div>
                        <div style={{ fontSize: 12.5, lineHeight: 1.5, color: '#5c574d', marginTop: 4 }}>{it.desc}</div>
                      </div>
                      {votable ? (
                        <button
                          onClick={() => toggle(it.id)}
                          disabled={busy === it.id}
                          aria-pressed={isVoted}
                          aria-label={`${isVoted ? 'Remove vote for' : 'Vote for'} ${it.title}`}
                          style={{
                            flexShrink: 0,
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 44,
                            padding: '7px 0 5px',
                            borderRadius: 10,
                            cursor: busy === it.id ? 'default' : 'pointer',
                            border: `1px solid ${isVoted ? '#7c5cff' : 'rgba(0,0,0,0.12)'}`,
                            background: isVoted ? 'rgba(124,92,255,0.10)' : '#fff',
                            color: isVoted ? '#7c5cff' : '#5c574d',
                            fontFamily: 'inherit',
                            transition: 'all 0.12s ease',
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 15l-6-6-6 6" />
                          </svg>
                          <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                        </button>
                      ) : (
                        <span aria-hidden="true" style={{ flexShrink: 0, color: '#10b981', marginTop: 2 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
